import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { CreditCard, Loader2, RefreshCw, XCircle, AlertTriangle, RotateCcw } from "lucide-react";
import { api } from "@/lib/api";
import { cn } from "@/lib/utils";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";

const STATUS = {
  active: { label: "Active", cls: "border-bloq-mint/40 bg-bloq-mint/10 text-bloq-mint" },
  trialing: { label: "Trial", cls: "border-bloq-blue/40 bg-bloq-blue/10 text-bloq-blue" },
  past_due: { label: "Payment issue", cls: "border-destructive/40 bg-destructive/10 text-destructive" },
};

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : null);

// cc_21: memberships are auto-renewing Stripe subscriptions — cancel (access to period end),
// resume, and the Stripe Customer Portal (card update / invoices). Renders only when manageable.
export function ManageSubscriptionCard({ sub, planName }) {
  const qc = useQueryClient();
  const [confirmCancel, setConfirmCancel] = useState(false);
  const st = STATUS[sub?.status] || STATUS.active;
  const ending = !!sub?.cancel_at_period_end;
  const when = fmtDate(sub?.renews_at || sub?.expires_at);
  const done = () => qc.invalidateQueries({ queryKey: ["subscription"] });

  const cancelMut = useMutation({
    mutationFn: api.subscriptionCancel,
    onSuccess: () => { done(); toast.success(`Auto-renew off — ${planName} stays active until ${when || "the end of your period"}.`); },
    onError: (e) => toast.error(e?.response?.data?.detail?.message || e?.response?.data?.detail || "Couldn't cancel — please try again."),
  });
  const resumeMut = useMutation({
    mutationFn: api.subscriptionResume,
    onSuccess: () => { done(); toast.success("Auto-renew is back on."); },
    onError: (e) => toast.error(e?.response?.data?.detail?.message || e?.response?.data?.detail || "Couldn't resume — please try again."),
  });
  const portalMut = useMutation({
    mutationFn: () => api.subscriptionPortal(`${window.location.origin}/app/membership`),
    onSuccess: (r) => { if (r?.url) window.location.href = r.url; else toast.error("Billing portal unavailable right now."); },
    onError: () => toast.error("Couldn't open the billing portal."),
  });
  if (!sub?.manageable) return null;

  return (
    <div className="mt-6 rounded-3xl border border-white/10 bg-bloq-s1 p-5 sm:p-6" data-testid="manage-subscription-card">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-sm font-bold text-white">{planName}</span>
        <span className={cn("rounded-full border px-2 py-0.5 text-[10px] font-bold uppercase", st.cls)} data-testid="subscription-status-pill">{st.label}</span>
        {sub.interval && <span className="text-xs text-bloq-muted">billed {sub.interval === "year" ? "yearly" : "monthly"}</span>}
      </div>
      <p className="mt-1.5 text-sm text-bloq-muted" data-testid="subscription-renewal-line">
        {ending ? `Auto-renew is off — access ends ${when || "at the end of this period"}.` : when ? `Renews ${when}.` : "Renews automatically."}
      </p>
      {sub.payment_issue && (
        <div className="mt-3 flex items-start gap-2 rounded-2xl border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive" data-testid="subscription-payment-issue">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          <span>Your last payment didn't go through. Update your card to keep {planName}.</span>
        </div>
      )}
      <div className="mt-4 flex flex-wrap gap-2">
        <button onClick={() => portalMut.mutate()} disabled={portalMut.isPending} className="inline-flex items-center gap-1.5 rounded-full border border-white/15 px-4 py-2 text-xs font-semibold text-white hover:bg-white/5 disabled:opacity-50" data-testid="subscription-portal-btn">
          {portalMut.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <CreditCard className="h-3.5 w-3.5" />} {sub.payment_issue ? "Update payment method" : "Manage billing"}
        </button>
        {ending ? (
          <button onClick={() => resumeMut.mutate()} disabled={resumeMut.isPending} className="inline-flex items-center gap-1.5 rounded-full border border-bloq-mint/40 bg-bloq-mint/10 px-4 py-2 text-xs font-semibold text-bloq-mint hover:bg-bloq-mint/20 disabled:opacity-50" data-testid="subscription-resume-btn">
            {resumeMut.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RotateCcw className="h-3.5 w-3.5" />} Resume auto-renew
          </button>
        ) : (
          <button onClick={() => setConfirmCancel(true)} disabled={cancelMut.isPending} className="inline-flex items-center gap-1.5 rounded-full border border-white/10 px-4 py-2 text-xs font-semibold text-bloq-muted hover:text-white disabled:opacity-50" data-testid="subscription-cancel-btn">
            {cancelMut.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <XCircle className="h-3.5 w-3.5" />} Cancel auto-renew
          </button>
        )}
        <span className="inline-flex items-center gap-1 self-center text-[11px] text-bloq-muted"><RefreshCw className="h-3 w-3" /> Switch plans from the cards below</span>
      </div>

      <AlertDialog open={confirmCancel} onOpenChange={setConfirmCancel}>
        <AlertDialogContent className="border-white/10 bg-bloq-s1 text-white" data-testid="cancel-subscription-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Cancel auto-renew?</AlertDialogTitle>
            <AlertDialogDescription className="text-bloq-muted">
              You keep {planName} until {when || "the end of the current period"}. No refund is issued, and you can resume any time before then.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-white/10 bg-transparent text-white hover:bg-white/5" data-testid="cancel-subscription-keep">Keep it</AlertDialogCancel>
            <AlertDialogAction onClick={() => cancelMut.mutate()} className="bg-destructive text-white hover:bg-destructive/90" data-testid="cancel-subscription-confirm">Cancel auto-renew</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
